import React, { useEffect, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { pickLocalized } from "../../../i18n/pickLocalized";
import * as crmApi from "../api/crmApi";
import { crmErrorMessage } from "../api/crmErrors";
import useTenantCurrency from "../../../lib/useTenantCurrency";
import SlideOver from "../ui/SlideOver";
import CrmInput from "../ui/CrmInput";
import CrmSelect from "../ui/CrmSelect";
import CrmButton from "../ui/CrmButton";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import { LABEL } from "../ui/classes";
import { CRM } from "../../../constants/testIds/crm";

const EMPTY = { name: "", sessionCount: "", price: "", validityMonths: "", serviceId: "" };

/**
 * The catalogue's price is a whole major-unit amount (the wireframe: "₹4,500", never
 * cents), the same round-trip `AssignPackDialog`'s "Record amount" already uses.
 */
const majorToMinor = (major) => Math.round(Number(String(major).replace(",", ".")) * 100);
const minorToMajor = (cents) => (Number(cents || 0) / 100).toString();

const toDraft = (pack) => (pack ? {
  name: pack.name || "",
  sessionCount: String(pack.session_count ?? ""),
  price: minorToMajor(pack.price_cents),
  validityMonths: pack.validity_months != null ? String(pack.validity_months) : "",
  serviceId: pack.service_id || "",
} : EMPTY);

/**
 * "+ Create pack" / row "Edit" (design §E.8): Name, Session count, Price (currency
 * prefix), Validity (months, optional), Linked session type — only services the admin
 * can book a fixed price for; a per-minute service cannot back a pack (C2.03 422
 * `SERVICE_PER_MINUTE`, the same rule `admin_create_booking` enforces).
 */
export default function PackFormSheet({ pack, onClose, onSaved }) {
  const t = useT("crm");
  const tc = useT("common");
  const { lang } = useLanguage();
  const currency = useTenantCurrency();
  const [services, setServices] = useState(null);
  const [servicesError, setServicesError] = useState(null);
  const [form, setForm] = useState(() => toDraft(pack));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const editing = !!pack;

  useEffect(() => { setForm(toDraft(pack)); setErrors({}); }, [pack]);

  useEffect(() => {
    let alive = true;
    crmApi.listBookingServices()
      .then((data) => {
        const list = Array.isArray(data) ? data : (data?.rows || []);
        if (alive) setServices(list.filter((s) => s.active !== false && s.pricing_mode !== "per_minute"));
      })
      .catch((e) => { if (alive) setServicesError(e); });
    return () => { alive = false; };
  }, []);

  const set = (key, value) => { setForm((f) => ({ ...f, [key]: value })); setErrors((e) => ({ ...e, [key]: undefined })); };

  const SCHEMA = z.object({
    name: z.string().trim().min(1, "packForm.errNameRequired").max(200, "packForm.errNameRequired"),
    sessionCount: z.string().refine((v) => Number.isInteger(Number(v)) && Number(v) >= 1, "packForm.errSessionCountInvalid"),
    price: z.string().refine((v) => Number.isFinite(Number(String(v).replace(",", "."))) && Number(String(v).replace(",", ".")) >= 0, "packForm.errPriceInvalid"),
    validityMonths: z.string().refine((v) => v === "" || (Number.isInteger(Number(v)) && Number(v) >= 1), "packForm.errValidityInvalid"),
    serviceId: z.string().min(1, "packForm.errServiceRequired"),
  });

  const save = async () => {
    const parsed = SCHEMA.safeParse(form);
    if (!parsed.success) {
      const next = {};
      parsed.error.issues.forEach((i) => { next[i.path[0]] = i.message; });
      setErrors(next);
      return;
    }
    // The tenant currency is fetched once per session and is normally already resolved by
    // the time this form is open; the guard exists for the narrow window right after
    // mount, so a pack is never priced in a guessed currency (useTenantCurrency's own
    // contract: null means "not known yet", never "none").
    if (!currency) { toast.error(crmErrorMessage(null, t, tc)); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        session_count: Number(form.sessionCount),
        price_cents: majorToMinor(form.price),
        currency,
        validity_months: form.validityMonths === "" ? null : Number(form.validityMonths),
        service_id: form.serviceId,
      };
      const saved = editing ? await crmApi.updatePack(pack.id, payload) : await crmApi.createPack(payload);
      toast.success(t(editing ? "packForm.updated" : "packForm.created"));
      onSaved?.(saved);
      onClose();
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  const err = (key) => errors[key] && <p role="alert" className="mt-1 text-xs text-red-700">{t(errors[key])}</p>;

  return (
    <SlideOver
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={t(editing ? "packForm.titleEdit" : "packForm.titleCreate")}
      testId={CRM.packForm}
      footer={(
        <CrmButton size="full" onClick={save} disabled={busy || !currency} data-testid={CRM.packFormSave}>{t("packForm.save")}</CrmButton>
      )}
    >
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save(); }} noValidate>
        <div>
          <label htmlFor="crm-pack-form-name" className={LABEL}>{t("packForm.name")}</label>
          <CrmInput id="crm-pack-form-name" value={form.name} onChange={(e) => set("name", e.target.value)}
            placeholder={t("packForm.namePlaceholder")} invalid={!!errors.name} data-testid={CRM.packFormName} />
          {err("name")}
        </div>
        <div>
          <label htmlFor="crm-pack-form-sessions" className={LABEL}>{t("packForm.sessionCount")}</label>
          <CrmInput id="crm-pack-form-sessions" inputMode="numeric" value={form.sessionCount}
            onChange={(e) => set("sessionCount", e.target.value)} invalid={!!errors.sessionCount} data-testid={CRM.packFormSessions} />
          {err("sessionCount")}
        </div>
        <div>
          <label htmlFor="crm-pack-form-price" className={LABEL}>{t("packForm.price")}</label>
          <div className="relative">
            <span aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-eh-muted uppercase">
              {currency}
            </span>
            <CrmInput id="crm-pack-form-price" inputMode="decimal" value={form.price} onChange={(e) => set("price", e.target.value)}
              invalid={!!errors.price} className="pl-12" data-testid={CRM.packFormPrice} />
          </div>
          {err("price")}
        </div>
        <div>
          <label htmlFor="crm-pack-form-validity" className={LABEL}>{t("packForm.validity")}</label>
          <CrmInput id="crm-pack-form-validity" inputMode="numeric" value={form.validityMonths}
            onChange={(e) => set("validityMonths", e.target.value)} invalid={!!errors.validityMonths} data-testid={CRM.packFormValidity} />
          {err("validityMonths")}
        </div>
        <div>
          <label htmlFor="crm-pack-form-service" className={LABEL}>{t("packForm.service")}</label>
          {servicesError && <CrmLoadFailure error={servicesError} />}
          <CrmSelect id="crm-pack-form-service" className="w-full" selectClassName="w-full" value={form.serviceId}
            onChange={(e) => set("serviceId", e.target.value)} placeholder={t("packForm.servicePlaceholder")}
            options={(services || []).map((s) => ({ value: s.id, label: pickLocalized(s, "name", lang) }))}
            data-testid={CRM.packFormService} />
          {err("serviceId")}
          <p className="mt-1 text-xs text-eh-muted">{t("packForm.perMinuteHint")}</p>
        </div>
      </form>
    </SlideOver>
  );
}
